import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { useLang } from "./LanguageProvider";
import { ArrowRight, Mail, MapPin, Phone, Smartphone, CheckCircle2, AlertCircle } from "lucide-react";
import { CONTACT_EMAIL } from "@/lib/siteConfig";

const ROLE_KEYS = ["investor", "mep", "procurement", "press"];

export default function Contact() {
  const { t, isRtl } = useLang();
  const formRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  const goToThankYou = () => {
    let lang = "en";
    try {
      lang = localStorage.getItem("flowwhite_lang") === "ar" ? "ar" : "en";
    } catch (_) {}
    window.location.href = `/thank-you/?lang=${lang}`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");

    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    // Graceful path when EmailJS is not yet configured: simulate success after short delay
    if (
      !serviceId ||
      !templateId ||
      !publicKey ||
      serviceId.startsWith("PLACEHOLDER")
    ) {
      console.warn("[FlowWhite] EmailJS env vars are placeholders. Submission logged locally only.");
      const formData = new FormData(formRef.current);
      const payload = Object.fromEntries(formData.entries());
      console.info("[FlowWhite] Contact submission:", payload);
      await new Promise((r) => setTimeout(r, 700));
      setStatus("sent");
      formRef.current?.reset();
      goToThankYou();
      return;
    }

    try {
      await emailjs.sendForm(serviceId, templateId, formRef.current, { publicKey });
      setStatus("sent");
      formRef.current?.reset();
      goToThankYou();
    } catch (err) {
      console.error("EmailJS send failed", err);
      setErrorMsg(err?.text || err?.message || "Unknown error");
      setStatus("error");
    }
  };

  const inputClass =
    "w-full bg-transparent border-b border-white/15 focus:border-brand-teal text-white placeholder-white/30 py-3.5 px-1 text-sm md:text-base outline-none transition-colors";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-brand-navy py-24 md:py-40 border-t border-white/5"
    >
      <div className="container mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left — copy + address */}
          <div className="lg:col-span-5">
            <div className="reveal flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-brand-teal" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-brand-teal font-semibold">
                {t.contact.overline}
              </span>
            </div>
            <h2 className="reveal reveal-delay-1 font-heading text-white text-3xl sm:text-4xl md:text-5xl lg:text-[52px] leading-[1.05] font-medium tracking-tight">
              {t.contact.title_investor}
            </h2>
            <p className={`reveal reveal-delay-2 mt-6 text-white/65 text-base md:text-lg leading-relaxed ${isRtl ? "text-right" : "text-left"}`}>
              {t.contact.lead}
            </p>

            <div className="reveal reveal-delay-3 mt-12 space-y-6">
              <div className="flex items-start gap-4">
                <span className="mt-1 inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/10 text-brand-teal flex-shrink-0">
                  <MapPin size={16} strokeWidth={1.6} />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-semibold">
                    {t.contact.address.title}
                  </div>
                  <div className="text-white text-base mt-1">{t.contact.address.line}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="mt-1 inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/10 text-brand-teal flex-shrink-0">
                  <Phone size={16} strokeWidth={1.6} />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-semibold">
                    {t.contact.address.phone_label}
                  </div>
                  <a
                    href={`tel:${t.contact.address.phone.replace(/\s/g, "")}`}
                    dir="ltr"
                    className="text-white text-base mt-1 block hover:text-brand-teal transition-colors"
                    data-testid="contact-phone-link"
                  >
                    {t.contact.address.phone}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="mt-1 inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/10 text-brand-teal flex-shrink-0">
                  <Smartphone size={16} strokeWidth={1.6} />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-semibold">
                    {t.contact.address.mobile_label}
                  </div>
                  <a
                    href={`tel:${t.contact.address.mobile.replace(/\s/g, "")}`}
                    dir="ltr"
                    className="text-white text-base mt-1 block hover:text-brand-teal transition-colors"
                    data-testid="contact-mobile-link"
                  >
                    {t.contact.address.mobile}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <span className="mt-1 inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/10 text-brand-teal flex-shrink-0">
                  <Mail size={16} strokeWidth={1.6} />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-semibold">
                    {t.contact.address.email_label}
                  </div>
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="text-white text-base mt-1 block hover:text-brand-teal transition-colors"
                    data-testid="contact-email-link"
                  >
                    {CONTACT_EMAIL}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="lg:col-span-7">
            <form
              ref={formRef}
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="reveal reveal-delay-1 rounded-2xl border border-white/10 bg-brand-surface p-7 md:p-12"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                <div>
                  <label htmlFor="contact-name" className="block text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold">
                    {t.contact.labels.name}
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    required
                    data-testid="contact-input-name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold">
                    {t.contact.labels.email}
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    data-testid="contact-input-email"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-mobile" className="block text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold">
                    {t.contact.labels.mobile}
                  </label>
                  <input
                    id="contact-mobile"
                    name="mobile"
                    type="tel"
                    autoComplete="tel"
                    dir="ltr"
                    data-testid="contact-input-mobile"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-company" className="block text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold">
                    {t.contact.labels.company}
                  </label>
                  <input
                    id="contact-company"
                    name="company"
                    data-testid="contact-input-company"
                    className={inputClass}
                  />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="contact-role" className="block text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold">
                    {t.contact.labels.role}
                  </label>
                  <select
                    id="contact-role"
                    name="role"
                    data-testid="contact-input-role"
                    defaultValue=""
                    className={inputClass + " appearance-none bg-brand-surface"}
                  >
                    <option value="" disabled>{isRtl ? "اختر..." : "Select..."}</option>
                    {t.contact.labels.role_options.map((opt, i) => (
                      <option key={ROLE_KEYS[i]} value={ROLE_KEYS[i]} className="bg-brand-surface">
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="contact-message" className="block text-[10px] uppercase tracking-[0.22em] text-white/45 font-semibold">
                    {t.contact.labels.message}
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={4}
                    data-testid="contact-input-message"
                    className={inputClass + " resize-none"}
                  />
                </div>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
                <div className="text-xs text-white/40 max-w-md leading-relaxed">
                  {isRtl
                    ? "بإرسال هذا النموذج فأنت توافق على أن نتواصل معك بخصوص استفسارك."
                    : "By submitting, you consent to be contacted regarding your enquiry."}
                </div>
                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="contact-submit-btn"
                  className="group inline-flex items-center justify-center gap-3 bg-brand-teal hover:bg-brand-tealDark disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-4 rounded-full text-sm md:text-base font-medium transition-all hover:-translate-y-0.5"
                >
                  {status === "sending" ? t.contact.labels.sending : t.contact.labels.send}
                  <ArrowRight size={16} className={`transition-transform group-hover:translate-x-1 ${isRtl ? "rotate-180 group-hover:-translate-x-1" : ""}`} />
                </button>
              </div>

              {status === "sent" && (
                <div
                  data-testid="contact-success"
                  className="mt-6 flex items-start gap-3 rounded-lg border border-brand-teal/40 bg-brand-teal/10 px-5 py-4 text-sm text-white"
                >
                  <CheckCircle2 size={18} className="text-brand-teal mt-0.5 flex-shrink-0" />
                  <span>{t.contact.labels.sent}</span>
                </div>
              )}
              {status === "error" && (
                <div
                  data-testid="contact-error"
                  className="mt-6 flex items-start gap-3 rounded-lg border border-red-500/40 bg-red-500/10 px-5 py-4 text-sm text-white"
                >
                  <AlertCircle size={18} className="text-red-400 mt-0.5 flex-shrink-0" />
                  <span>{t.contact.labels.error}{errorMsg ? `: ${errorMsg}` : ""}</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
